import type { App } from 'vue'
import analyticsService from '@services/analytics-service'
import consentService from '@services/consent-service'
import mixpanelService from '@services/mixpanel-service'
import superPropertiesManager from '@services/mixpanel/super-properties-manager'
import appLoadTracker from '@services/mixpanel/app-load-tracker'
import playbackTracker from '@services/mixpanel/playback-tracker'
import { analyticsKey } from '@composables/use-analytics'
import { useAuthStore } from '@state/stores/auth'

// Reads the auth store, so this installs after `app.use(pinia)`.
function initMixpanel() {
    const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN
    if (!mixpanelToken) return

    mixpanelService.init(mixpanelToken, {
        debug: import.meta.env.DEV,
        persistence: 'localStorage',
        track_pageview: false,
        batch_requests: true,
        opt_out_tracking_by_default: true,
    })

    consentService.restoreConsentState()

    // Auto-grant consent for logged-in users without consent (grandfathered)
    if (!consentService.hasConsent()) {
        const currentUser = useAuthStore().currentUser
        if (currentUser) {
            consentService.grantConsent('grandfathered')
        }
    }

    superPropertiesManager.initialize()

    const currentUser = useAuthStore().currentUser
    if (currentUser) {
        const memberId = currentUser.members_id?.id ? String(currentUser.members_id.id) : null
        if (memberId) {
            superPropertiesManager.updateUserStatus(true, memberId)
            mixpanelService.identify(memberId)
        }
    }

    appLoadTracker.start()

    // Report any listening from a session that crashed without a clean end (once, at boot).
    playbackTracker.recoverJournaledSession()

    playbackTracker.registerLifecycleListeners()
}

export default {
    install(app: App) {
        analyticsService.init()
        app.provide(analyticsKey, analyticsService)

        initMixpanel()
    },
}
